import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from './services/auth.service';
import { Router } from '@angular/router';

// Auth Guard
const authGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  
  if (authService.isLoggedIn()) {
    return true;
  }
  return router.createUrlTree(['/']);
};

// Admin Guard
const adminGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  
  if (authService.isAdmin()) {
    return true;
  }
  return router.createUrlTree(['/devices']);
};

// Kunden-Guard: nur Rolle kunde (für /customer), inkl. Admin-Vorschau
const customerGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.isKunde()) {
    return true;
  }
  return router.createUrlTree(['/devices']);
};

// Installateur-Guard: nur Rolle installer (für /installer), inkl. Admin-Vorschau
const installerGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.isInstaller()) {
    return true;
  }
  return router.createUrlTree(['/devices']);
};

// Staff Guard erweitert: Installateur nicht zugelassen
const staffGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const role = authService.user()?.role;
  if (role === 'installer') {
    return router.createUrlTree(['/installer']);
  }
  if (role === 'admin' || role === 'sales' || role === 'support' || role === 'manager') {
    return true;
  }
  return router.createUrlTree(['/customer']);
};

// Executive-Dashboard: Manager, Vertrieb, Support, Admin
const executiveDashboardGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.canViewExecutiveDashboard()) {
    return true;
  }
  return router.createUrlTree(['/devices']);
};

// Admin oder Vertrieb – für Kundenverwaltung
const adminOrSalesGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.user()?.role === 'installer') {
    return router.createUrlTree(['/installer']);
  }
  if (authService.canManageCustomers()) {
    return true;
  }
  return router.createUrlTree(['/devices']);
};

// Lead-CRM: Admin, Manager, Vertrieb (ohne Support)
const leadsGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.user()?.role === 'installer') {
    return router.createUrlTree(['/installer']);
  }
  if (authService.canManageLeads()) {
    return true;
  }
  return router.createUrlTree(['/devices']);
};

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./features/landing/landing.component').then(m => m.LandingComponent),
  },
  {
    path: 'sign/:token',
    loadComponent: () => import('./features/contract-signing/contract-signing.component').then(m => m.ContractSigningComponent),
  },
  {
    path: 'devices',
    loadComponent: () => import('./features/devices/devices-list.component').then(m => m.DevicesListComponent),
    canActivate: [authGuard, staffGuard],
  },
  {
    path: 'bestand',
    loadComponent: () => import('./features/inventory/inventory.component').then(m => m.InventoryComponent),
    canActivate: [authGuard, staffGuard],
  },
  {
    path: 'bestand/:id',
    loadComponent: () => import('./features/inventory/inventory-detail.component').then(m => m.InventoryDetailComponent),
    canActivate: [authGuard, staffGuard],
  },
  {
    path: 'overview',
    loadComponent: () => import('./features/manager-dashboard/manager-dashboard.component').then(m => m.ManagerDashboardComponent),
    canActivate: [authGuard, executiveDashboardGuard],
  },
  {
    path: 'customer/:organizationId',
    loadComponent: () => import('./features/customer/customer.component').then(m => m.CustomerComponent),
    canActivate: [authGuard, staffGuard],
  },
  {
    path: 'customer',
    loadComponent: () => import('./features/customer/customer.component').then(m => m.CustomerComponent),
    canActivate: [authGuard, customerGuard],
  },
  {
    path: 'devices/:id',
    loadComponent: () => import('./features/devices/device-detail.component').then(m => m.DeviceDetailComponent),
    canActivate: [authGuard],
  },
  {
    path: 'appointments',
    loadComponent: () => import('./features/register-device/appointments.component').then(m => m.AppointmentsComponent),
    canActivate: [authGuard, staffGuard],
  },
  {
    path: 'requests',
    loadComponent: () => import('./features/registration-requests/registration-requests.component').then(m => m.RegistrationRequestsComponent),
    canActivate: [authGuard, staffGuard],
  },
  {
    path: 'display-setup',
    loadComponent: () => import('./features/installation/installation.component').then(m => m.InstallationComponent),
    canActivate: [authGuard, staffGuard],
  },
  {
    path: 'admin',
    loadComponent: () => import('./features/admin/admin.component').then(m => m.AdminComponent),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: 'users',
    loadComponent: () => import('./features/users/users.component').then(m => m.UsersComponent),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: 'leads',
    loadComponent: () => import('./features/leads/leads.component').then(m => m.LeadsComponent),
    canActivate: [authGuard, leadsGuard],
  },
  {
    path: 'leads/:id',
    loadComponent: () => import('./features/leads/lead-detail.component').then(m => m.LeadDetailComponent),
    canActivate: [authGuard, leadsGuard],
  },
  {
    path: 'sales-proposals',
    loadComponent: () => import('./features/sales-proposals/sales-proposals.component').then(m => m.SalesProposalsComponent),
    canActivate: [authGuard, leadsGuard],
  },
  {
    path: 'sales-proposals/follow-ups',
    loadComponent: () => import('./features/sales-proposals/sales-proposal-follow-ups.component').then(m => m.SalesProposalFollowUpsComponent),
    canActivate: [authGuard, leadsGuard],
  },
  {
    path: 'sales-proposals/new',
    loadComponent: () => import('./features/sales-proposals/sales-proposal-wizard.component').then(m => m.SalesProposalWizardComponent),
    canActivate: [authGuard, leadsGuard],
  },
  {
    path: 'sales-proposals/:id',
    loadComponent: () => import('./features/sales-proposals/sales-proposal-wizard.component').then(m => m.SalesProposalWizardComponent),
    canActivate: [authGuard, leadsGuard],
  },
  {
    path: 'customers',
    loadComponent: () => import('./features/customers/customers.component').then(m => m.CustomersComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'customers/:id',
    loadComponent: () => import('./features/customers/customer-detail.component').then(m => m.CustomerDetailComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'location-partners',
    loadComponent: () => import('./features/location-partners/location-partners.component').then(m => m.LocationPartnersComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'location-partners/:id',
    loadComponent: () => import('./features/location-partners/location-partner-detail.component').then(m => m.LocationPartnerDetailComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'price-packages',
    loadComponent: () => import('./features/price-packages/price-packages.component').then(m => m.PricePackagesComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'price-packages/new',
    loadComponent: () => import('./features/price-packages/price-package-editor.component').then(m => m.PricePackageEditorComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'price-packages/:id',
    loadComponent: () => import('./features/price-packages/price-package-editor.component').then(m => m.PricePackageEditorComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'installation',
    loadComponent: () => import('./features/installation/installation-hub.component').then(m => m.InstallationHubComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'installers',
    redirectTo: 'installation?tab=installers',
    pathMatch: 'full',
  },
  {
    path: 'installers/:id',
    loadComponent: () => import('./features/installers/installer-detail.component').then(m => m.InstallerDetailComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'installation-jobs',
    redirectTo: 'installation?tab=jobs',
    pathMatch: 'full',
  },
  {
    path: 'installation-jobs/:id',
    loadComponent: () => import('./features/installation-jobs/installation-job-detail.component').then(m => m.InstallationJobDetailComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'contracts',
    loadComponent: () => import('./features/contracts/contracts-hub.component').then(m => m.ContractsHubComponent),
    canActivate: [authGuard, adminOrSalesGuard],
  },
  {
    path: 'contract-templates',
    redirectTo: '/contracts?tab=templates',
    pathMatch: 'full',
  },
  {
    path: 'customer-contracts',
    redirectTo: '/contracts?tab=customer',
    pathMatch: 'full',
  },
  {
    path: 'installer',
    loadComponent: () => import('./features/installer/installer-jobs.component').then(m => m.InstallerJobsComponent),
    canActivate: [authGuard, installerGuard],
  },
  {
    path: 'installer/jobs/:id',
    loadComponent: () => import('./features/installer/installer-job-detail.component').then(m => m.InstallerJobDetailComponent),
    canActivate: [authGuard, installerGuard],
  },
  {
    path: 'regions',
    loadComponent: () => import('./features/regions/regions.component').then(m => m.RegionsComponent),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: 'verify',
    loadComponent: () => import('./features/verify-email/verify-email.component').then(m => m.VerifyEmailComponent),
  },
  {
    path: 'profile',
    loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent),
    canActivate: [authGuard],
  },
];

